<template>
  <div class="modern-form patient-schedule">
    <div class="info-section">
      <h3 class="section-title">Schedule Appointment</h3>
      
      <ServiceMessage page="scheduling" />
      
      <div v-if="apptSchedulingLoading" class="booking-loading">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
          class="loading-spinner" />
      </div>
      
      <div v-else class="modern-stepper">
        <v-stepper v-model="currentStep" vertical class="stepper-content">
          <!-- Step 1: Select Patient -->
          <v-stepper-step
            :complete="currentStep > 1"
            step="1"
            @click="setStepIfBeyond(1)"
            class="step-header">
            Select a patient to schedule
            <span v-if="currentStep > 1" class="step-summary">- {{`${selectedPatient.firstName} ${selectedPatient.lastName}`}}</span>
          </v-stepper-step>
          <v-stepper-content step="1" class="step-content">
            <div class="patient-selection">
              <v-progress-circular
                v-if="patientsLoading"
                indeterminate
                color="primary"
                class="loading-spinner" />
              <div v-if="!patientsLoading" class="patient-list">
                <div
                  v-for="(item, i) in patientList"
                  :key="i"
                  @click="selectPatient(item)"
                  class="patient-item"
                  :class="{ selected: selectedPatient?.id === item.id }">
                  <div class="patient-card">
                    <div class="patient-avatar">
                      {{item.firstName[0] + item.lastName[0]}}
                    </div>
                    <div class="patient-name">
                      {{`${item.firstName} ${item.lastName}`}}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-stepper-content>

          <!-- Step 2: Appointment Type -->
          <v-stepper-step
            :complete="currentStep > 2"
            step="2"
            @click="setStepIfBeyond(2)"
            class="step-header">
            What is the reason for the appointment?
            <span v-if="currentStep > 2" class="step-summary"> - {{ selectedAppointmentType.name }} <span class="step-detail"> ({{ selectedAppointmentType.appointmentLength }} minutes)</span></span>
          </v-stepper-step>
          <v-stepper-content step="2" class="step-content">
            <div class="appointment-type-selection">
              <v-progress-circular
                v-if="apptTypeLoading"
                indeterminate
                color="primary"
                class="loading-spinner" />
              <div v-if="!apptTypeLoading">
                <div v-if="selectedPatient?.restricted" class="restriction-notice">
                  <h3>The selected account is currently unable to schedule appointments online.</h3>
                </div>
                <div v-else class="appointment-type-list">
                  <div
                    v-for="(item, i) in appointmentTypes"
                    :key="i"
                    @click="selectAppointmentType(item)"
                    class="appointment-type-item"
                    :class="{ selected: selectedAppointmentType?.id === item.id }">
                    <div class="appointment-type-card">
                      {{ item.name }} ({{ item.appointmentLength }} minutes)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-stepper-content>

          <!-- Step 3: Find Appointment -->
          <v-stepper-step
            :complete="currentStep > 3"
            step="3"
            @click="setStepIfBeyond(3)"
            class="step-header">
            Find an appointment
          </v-stepper-step>
          <v-stepper-content step="3" class="step-content">
            <div class="appointment-finder">
              <v-progress-circular
                v-if="availabilityLoading"
                indeterminate
                color="primary"
                class="loading-spinner" />
              <div v-if="!availabilityLoading" class="finder-content">
                <!-- No Providers Available -->
                <div v-if="!providers.length" class="no-providers-available">
                  <div class="no-providers-icon">
                    <v-icon size="48" color="#9ca3af">mdi-account-off</v-icon>
                  </div>
                  <h4 class="no-providers-title">No Providers Available</h4>
                  <p class="no-providers-message">
                    There are currently no providers available for the selected appointment type. Please contact the office to schedule your appointment.
                  </p>
                </div>
                
                <!-- Normal Provider Selection Flow -->
                <div v-if="providers.length" class="provider-selection">
                  <h4 class="selection-title">Preferred Provider</h4>
                  <div class="provider-list">
                    <div
                      v-for="(item, i) in providers"
                      :key="i"
                      @click="selectProvider(item)"
                      class="provider-item"
                      :class="{ active: item.id === selectedProvider?.id }">
                      <div class="provider-card">
                        <div class="provider-avatar">
                          <img :src="item.imageUrl" alt="Avatar" @error="$event.target.src='/static/img/generic_person.png'">
                        </div>
                        <div class="provider-name">{{item.firstName}} {{item.lastName}}</div>
                      </div>
                    </div>
                  </div>
                </div>
                  
                <div :class="{ 'date-selection': true, 'no-provider': !providers.length }">
                  <v-date-picker
                    v-model="datePickerSelection"
                    class="modern-date-picker"
                    :allowed-dates="isDateAllowed"
                    @change="dateSelected"
                    @click:month="getAvailabilityForAppointmentType"
                    ref="datepicker" />
                </div>
                  
                <div v-if="providers.length" class="time-selection">
                  <h4 v-if="slotsForDate" class="selection-title">Select a time slot for your appointment</h4>
                  <div v-if="slotsForDate && slotsForDate.length" class="time-slots">
                    <button
                      v-for="(item, i) in slotsForDate"
                      :key="i"
                      @click="selectTime(item)"
                      class="time-slot"
                      :class="{ selected: selectedTime === item.timeString }">
                      {{item.timeString}}
                    </button>
                  </div>
                  <div v-else-if="slotsForDate && slotsForDate.length === 0" class="no-appointments">
                    <div class="no-appointments-icon">
                      <v-icon size="48" color="#9ca3af">mdi-calendar-remove</v-icon>
                    </div>
                    <h4 class="no-appointments-title">No Available Appointments</h4>
                    <p class="no-appointments-message">
                      There are no available appointment slots for the selected date and provider. Please try selecting a different date or provider.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </v-stepper-content>

          <!-- Step 4: Book Appointment -->
          <v-stepper-step step="4" class="step-header">Book your appointment</v-stepper-step>
          <v-stepper-content step="4" class="step-content">
            <div v-if="currentStep >= 4" class="booking-summary">
              <div class="summary-section">
                <h4 class="summary-title">Appointment Type</h4>
                <div class="summary-content">
                  <span class="appointment-name">{{ selectedAppointmentType.name }}</span>
                  <span class="appointment-duration"> ({{ selectedAppointmentType.appointmentLength }} minutes)</span>
                </div>
              </div>

              <div class="summary-section">
                <h4 class="summary-title">Date & Time</h4>
                <div class="summary-content">
                  {{ selectedDate.toDateString() }} at {{ selectedTime }}
                </div>
              </div>

              <div class="summary-section">
                <h4 class="summary-title">Provider</h4>
                <div class="summary-content">
                  {{selectedProvider.firstName}} {{selectedProvider.lastName}}
                </div>
              </div>

              <div v-if="slotsForDate" class="notes-section">
                <div class="form-group">
                  <h4 class="notes-title" v-if="selectedAppointmentType.requireNote">
                    Please provide the reason for this appointment
                  </h4>
                  <h4 class="notes-title" v-else>
                    Add an additional comment for appointment?
                  </h4>
                  <v-text-field 
                    v-model="additionalNote" 
                    :required="selectedAppointmentType.requireNote"
                    class="line-input custom-filled"
                    dense
                    hide-details="auto" />
                </div>
              </div>

              <div class="booking-actions">
                <v-btn
                  class="btn-primary"
                  large
                  @click="bookAppointment()">
                  <v-icon left size="18">mdi-calendar-plus</v-icon>
                  Book Appointment
                </v-btn>
              </div>
            </div>
          </v-stepper-content>
        </v-stepper>
      </div>
    </div>

    <!-- Success Dialog -->
    <v-dialog v-model="dialog" max-width="500" persistent>
      <v-card class="modern-modal">
        <v-card-title class="modal-title">
          <v-icon color="#10b981" size="24" class="mr-3">mdi-check-circle</v-icon>
          Appointment Scheduled!
        </v-card-title>
        <v-card-text class="modal-content">
          <p class="modal-question">
            Your appointment has been scheduled successfully! If you need to cancel or reschedule, please call the office.
          </p>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-btn
            class="btn-primary"
            @click="acknowledgeConfirm()">
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import DataService from '../../../_services/DataService';
import ServiceMessage from '../../components/ServiceMessage';

export default {
  name: 'PatientSchedule',
  props: ['template_props'],
  components: {
    ServiceMessage,
  },
  data() {
    return {
      pageName: 'Patient Schedule',
      currentStep: 1,
      dialog: false,
      patientList: [],
      providers: [],
      patientsLoading: true,
      apptTypeLoading: false,
      providerLoading: true,
      datePickerSelection: new Date().toISOString().substring(0, 7),
      selectedProvider: null,
      appointmentTypes: [],
      appointmentType: '',
      attemptedAppointmentLoad: false,
      availableAppointmentSlots: [],
      slotsForDate: [],
      availabilityLoading: false,
      selectedDate: null,
      selectedTime: '',
      additionalNote: '',
      selectedOperatory: null,
      selectedPatient: null,
      selectedAppointmentType: null,
      apptSchedulingLoading: false,
      timeIncrementPreference: 10,
    };
  },
  methods: {
    setStepIfBeyond(step) {
      if (this.currentStep > step) {
        this.currentStep = step;
      }
    },
    async selectProvider(item) {
      this.selectedProvider = item;
      this.getAppointmentSlotsForCurrentDateSelection();
    },
    async getAppointmentSlots() {
      this.appointmentSlotsLoading = true;
      let params = {};

      params['ProvNum'] = this.selectedDentist;
      params['procedureCode'] = this.appointmentType.ProcCode;
      params['date'] = this.picker;
      
      // planned appointment if it has these two
      params['aptNum'] = this.appointmentType.AptNum;
      params['ProcTime'] = this.appointmentType.ProcTime;

      const response = await DataService.get_appointment_slots(params);
      if (response.status != 200) {
        let alert = {
          color: 'warning',
          text: 'Something went wrong!',
        };
        this.$root.$emit('raise_alert', alert);
        return;
      }
      this.appointmentSlots = response.data.data;
      this.countAppointmentSlots = this.appointmentSlots.length;
      this.appointmentSlotsLoading = false;
    },
    async getPatientFamily() {
      this.patientsLoading = true;
      this.patientList = [];
      const response = await DataService.getPatientFamily();
      if (response.status === 200) {
        this.patientList = response.data;

        if (this.patientList.length === 1) {
          // just the one patient, no need to show a list
          this.selectPatient(this.patientList[0]);
        }
      } else {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: 'There was an error getting family info',
        });
      }

      this.patientsLoading = false;
    },
    async getAppointmentTypes() {
      this.apptTypeLoading = true;
      const clientId = DataService.getClientId();
      const patientAge = (new Date() - new Date(this.selectedPatient.birthDate)) / (1000 * 60 * 60 * 24 * 365);
      const excludeMinors = patientAge >= 18;
      const onlyMinors = patientAge < 18;

      const response = await DataService.getSchedulableAppointmentTypes(clientId, { excludeMinors, onlyMinors, excludeNewPatient: true });

      this.appointmentTypes = response.data;
      this.apptTypeLoading = false;
    },
    async selectPatient(item) {
      this.currentStep = 2;
      this.selectedPatient = item;
      await this.getAppointmentTypes();
    },
    async selectAppointmentType(item) {
      this.currentStep = 3;
      this.selectedAppointmentType = item;
      this.getAvailabilityForAppointmentType();
    },
    async getAvailabilityForAppointmentType() {
      this.availabilityLoading = true;

      let startDate = new Date();
      // we've already loaded data; this load is being caused by a time selection change
      if (this.attemptedAppointmentLoad && this.$refs.datepicker) {
        startDate = new Date(this.$refs.datepicker.tableYear, this.$refs.datepicker.tableMonth, 1);
      }

      this.attemptedAppointmentLoad = true;

      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + 1, 0); // set as end of month

      const clientId = DataService.getClientId();
      const startDateString = moment(startDate).format('YYYY-MM-DD'); // don't need to shift to UTC time by using toISOString()
      const endDateString = moment(endDate).format('YYYY-MM-DD');

      let response;
      if (this.selectedAppointmentType.id) {
        response = await DataService.getAvailabilityForAppointmentType(clientId, this.selectedAppointmentType.id, startDateString, endDateString);
      } else {
        response = await DataService.getAvailabilityForCustomAppointment(clientId, this.selectedProvider.id, this.selectedAppointmentType.appointmentLength, startDateString, endDateString, this.selectedAppointmentType.pattern);
      }

      if (response.status !== 200) {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: response.data || 'An error occurred retrieving scheduling data',
        });
        this.currentStep = this.currentStep - 1;
        this.availabilityLoading = false;
        return;
      }

      this.timeIncrementPreference = response.data.timeIncrementPreference;
      this.providers = response.data.providers;

      for (const provider of this.providers) {
        provider.imageUrl = `${window.acrew.apiUrl}/clients/${clientId}/providers/${provider.id}/image`;
      }

      if (this.providers.length > 1) {
        this.providers.unshift({ id: -1, firstName: 'Any', imageUrl: '/static/img/generic_person.png' });
      }

      this.selectedProvider = this.providers[0];

      this.availableAppointmentSlots = response.data.slots;

      if (this.availableAppointmentSlots.length) {
        // set to the first date (today typically)
        const firstDateString = this.availableAppointmentSlots[0].start.substring(0, 10);
        const [year, month, day] = firstDateString.split('-');
        this.selectedDate = new Date(Number(year), Number(month) - 1, Number(day));
        this.datePickerSelection = firstDateString;
        this.getAppointmentSlotsForCurrentDateSelection();
      } else {
        this.selectedDate = startDate;
        this.datePickerSelection = startDateString;
      }

      this.availabilityLoading = false;

      this.bindMonthClickHandlers();
    },
    getAppointmentSlotsForCurrentDateSelection() {
      const appointmentSlotsForDate = this.availableAppointmentSlots.filter((slot) => {
        if (this.selectedProvider?.id > 0 && slot.providerId !== this.selectedProvider.id) {
          return false;
        }

        const [year, month, day] = slot.start.substring(0, 10).split('-');
        const slotDate = new Date(Number(year), Number(month) - 1, Number(day));
        return slotDate.getTime() === this.selectedDate.getTime();
      });

      const appointmentTypeLength = this.selectedAppointmentType.appointmentLength;

      const uniqueSlots = {};
      for (const slot of appointmentSlotsForDate) {
        const endDate = new Date(slot.end);

        let dateCopy;
        let loop = 0; // track position and also prevent overflow

        do {
          dateCopy = new Date(slot.start);
          dateCopy.setMinutes(dateCopy.getMinutes() + this.timeIncrementPreference * loop);

          const slotTime = dateCopy.getTime();
          if (!uniqueSlots[slotTime]) {
            uniqueSlots[slotTime] = {
              time: slotTime,
              providerId: slot.providerId,
              operatoryId: slot.operatoryId,
            };
          }
        } while (++loop < 50 && dateCopy.setMinutes(dateCopy.getMinutes() + appointmentTypeLength) < endDate);
      }

      const anHourFromNow = new Date();
      anHourFromNow.setMinutes(anHourFromNow.getMinutes() + 60);

      this.slotsForDate = [];
      const timeSortedKeys = Object.keys(uniqueSlots).sort();
      for (const key of timeSortedKeys) {
        const slot = new Date(uniqueSlots[key].time);

        // don't allow past scheduling, and no sooner than an hour from now
        if (slot.getTime() < anHourFromNow) {
          continue;
        }

        const hourPiece = slot.getHours() % 12 || '12';
        const minutePiece = slot.getMinutes().toString().padStart(2, '0');
        const amPm = slot.getHours() >= 12 ? 'PM' : 'AM';

        this.slotsForDate.push({
          ...uniqueSlots[key],
          timeString: `${hourPiece}:${minutePiece} ${amPm}`,
        });
      }
    },
    isDateAllowed(value) {
      if (!this.availableAppointmentSlots.length) {
        return false;
      }

      const [year, month, day] = value.split('-');
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return this.availableAppointmentSlots.some((slot) => {
        if (this.selectedProvider?.id > 0 && slot.providerId !== this.selectedProvider.id) {
          return false;
        }

        const [loopYear, loopMonth, loopDay] = slot.start.substring(0, 10).split('-');
        const slotDate = new Date(Number(loopYear), Number(loopMonth) - 1, Number(loopDay));
        return slotDate.getTime() === date.getTime();
      });
    },
    dateSelected(date) {
      const [year, month, day] = date.split('-');
      this.selectedDate = new Date(Number(year), Number(month) - 1, Number(day));
      this.getAppointmentSlotsForCurrentDateSelection();
    },
    selectTime(item) {
      this.selectedTime = item.timeString;
      this.selectedProvider = this.providers.find((prov) => prov.id === item.providerId);
      this.selectedOperatory = item.operatoryId;

      this.currentStep = 4;
    },
    async bookAppointment() {
      if (!this.additionalNote && this.selectedAppointmentType.requireNote) {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: 'The selected appointment type requires a note be provided stating the reason for the appointment',
        });
        return;
      }

      this.apptSchedulingLoading = true;

      const [, hour, minute, amPm] = this.selectedTime.match(/(\d+):(\d+) ([AP]M)/);
      let normalizedHour = Number(hour) + (amPm === 'PM' ? 12 : 0);
      if (!(normalizedHour % 12)) {
        normalizedHour -= 12;
      }

      const appointmentDateString = `${this.selectedDate.getFullYear()}-${(this.selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${(this.selectedDate.getDate()).toString().padStart(2, '0')}T${normalizedHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')}:00.000`; 

      const body = {
        providerId: this.selectedProvider.id,
        operatoryId: this.selectedOperatory,
        appointmentDate: appointmentDateString,
        appointmentTypeId: this.selectedAppointmentType.id,
        plannedAppointmentId: this.$route.query.plannedAppointmentId,
        codes: this.$route.query.codes ? JSON.parse(this.$route.query.codes) : undefined,
        additionalNote: this.additionalNote,
        appointmentLength: this.selectedAppointmentType.appointmentLength,
      };

      const maybeId = this.selectedPatient.status === 'Self' ? undefined : this.selectedPatient.id;
      const resp = await DataService.scheduleAppointmentForPatient(body, maybeId);

      if (resp.status == 201) {
        this.dialog = true;
      } 
      else {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: resp.data || 'Something went wrong. Please try again later.',
        });
      }

      this.apptSchedulingLoading = false;
    },
    async acknowledgeConfirm() {
      this.dialog = false;

      this.selectedPatient = [];
      this.appointmentType = [];
      this.selectedDentist = [];
      this.selectedAppointmentSlot = [];
      this.currentStep = 1;

      this.$router.push({
        name: 'Dashboard',
      });
    },
    bindMonthClickHandlers() {
      // vuetify 2 doesn't offer us a way to capture month change via the prev/next button so... guess we're doing that ourselves
      // have to do wait for render and redo this on each load too weeeee
      setTimeout(() => {
        if (!this.$refs.datepicker?.$el) return;
        const prevBtn = this.$refs.datepicker.$el.querySelector('.v-btn[aria-label="Previous month"]');
        const nextBtn = this.$refs.datepicker.$el.querySelector('.v-btn[aria-label="Next month"]');
        if (prevBtn) prevBtn.addEventListener('click', this.getAvailabilityForAppointmentType);
        if (nextBtn) nextBtn.addEventListener('click', this.getAvailabilityForAppointmentType);
      }, 200);
    },
  },
  async mounted() {
    await this.getPatientFamily();

    if (this.$route.query.patientId && this.patientList.length > 1) {
      const patient = this.patientList.find((pat) => pat.id === Number(this.$route.query.patientId));
      if (patient) {
        this.selectPatient(patient);
      }
    }

    if (this.$route.query.providerId && this.$route.query.duration) {
      this.currentStep = 3;
      this.selectedProvider = { id: this.$route.query.providerId };
      this.selectedAppointmentType = { name: this.$route.query.description, pattern: this.$route.query.pattern, appointmentLength: Number(this.$route.query.duration) };

      await this.getAvailabilityForAppointmentType();
    }
  },
};
</script>

<style lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

// Component-specific color variables
// Stepper colors
$stepper-step-bg-color: #10b981;
$stepper-step-text-color: white;
$stepper-step-active-bg-color: #b0d35e;
$stepper-step-active-text-color: #414a5b;
$stepper-step-default-bg-color: #e5e7eb;
$stepper-step-default-text-color: #6b7280;
$step-summary-color: #6b7280;
$step-detail-color: #9ca3af;

// Patient card colors
$patient-card-bg-color: white;
$patient-card-border-color: #e2e8f0;
$patient-card-hover-color: #f8fafc;
$patient-card-active-color: #b0d35e;
$patient-avatar-bg-color: #133b54;
$patient-avatar-text-color: white;
$patient-name-text-color: #374151;

// Appointment type colors
$appointment-card-bg-color: white;
$appointment-card-border-color: #e2e8f0;
$appointment-card-hover-color: #f8fafc;
$appointment-card-active-color: #b0d35e;
$appointment-type-text-color: #374151;
$restriction-notice-bg-color: #fef2f2;
$restriction-notice-border-color: #fecaca;
$restriction-notice-text-color: #dc2626;

// Provider card colors
$provider-card-bg-color: white;
$provider-card-border-color: #e2e8f0;
$provider-card-active-color: #eff6ff;
$provider-card-active-border-color: #b0d35e;
$provider-name-text-color: #374151;

// Time slot colors
$time-slot-bg-color: white;
$time-slot-border-color: #d1d5db;
$time-slot-hover-color: #f3f4f6;
$time-slot-active-color: #b0d35e;
$time-slot-text-color: #374151;

// Summary section colors
$summary-title-color: #374151;
$summary-content-color: #6b7280;
$appointment-duration-color: #9ca3af;
$notes-title-text-color: #374151;

// General colors
$selection-title-color: #374151;
$date-picker-border-color: #d1d5db;

// Mixins
@mixin card-hover-effect {
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #edf2f7;
    border-color: #cbd5e0;
  }
}

@mixin card-base {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  @include card-hover-effect;
  
  @media (max-width: $breakpoint-mobile) {
    border-radius: 8px;
  }
}

.patient-schedule {
  @include mobile-nav-spacing;
  padding: 16px;

  .info-section {
    @media (max-width: $breakpoint-mobile) {
      padding: 12px;
    }
  }

  .modern-stepper {
    background: transparent !important;
    box-shadow: none !important;
  }

  .stepper-content {
    .v-stepper__step {
      &.v-stepper__step--complete {
        cursor: pointer;
      }

      .v-stepper__step__step {
        width: 28px !important;
        height: 28px !important;
        min-width: 28px !important;
        background-color: $stepper-step-default-bg-color !important;
        color: $stepper-step-default-text-color !important;
        font-weight: 600 !important;
        font-size: 13px !important;
        border-radius: 50% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.2s ease !important;
      }

      &.v-stepper__step--active {
        .v-stepper__step__step {
          background-color: $stepper-step-active-bg-color !important;
          color: $stepper-step-active-text-color !important;
          box-shadow: 0 0 0 3px rgba(176, 211, 94, 0.2) !important;
        }
      }

      &.v-stepper__step--complete {
        .v-stepper__step__step {
          background-color: $stepper-step-bg-color !important;
          color: $stepper-step-text-color !important;

          > i.v-icon.mdi-check {
            font-size: 1.15rem !important;
          }
        }
      }
    }
  }

  .step-header {
    @media (max-width: $breakpoint-mobile) {
      padding: 16px 0 !important;

      .v-stepper__label {
        font-size: 14px !important;
        line-height: 1.4 !important;
        padding-left: 8px !important;
      }
    }

    .step-summary {
      color: $step-summary-color;
      font-weight: 500;

      .step-detail {
        color: $step-detail-color;
      }
    }
  }

  .step-content {
    padding: 24px 0;

    @media (max-width: $breakpoint-mobile) {
      border-left: none !important;
      margin: 0 !important;
      padding: 0 5px !important;
    }
  }

  .loading-spinner {
    display: block;
    margin: 40px auto;
  }

  .booking-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }

  // Patient Selection
  .patient-selection {
    .patient-list {
      display: grid;
      gap: 16px;

      @media (max-width: $breakpoint-mobile) {
        gap: 12px;
      }

      .patient-item {
        cursor: pointer;
        
        &.selected .patient-card {
          background: #f0f9e8 !important;
          border-color: #B0D35E !important;
          
          .patient-name {
            color: #414a5b !important;
            font-weight: 600 !important;
          }
          
          .patient-avatar {
            background: #133b54 !important;
          }
        }

        .patient-card {
          display: flex;
          align-items: center;
          padding: 12px 16px;
          @include card-base;

          @media (max-width: $breakpoint-mobile) {
            padding: 12px 16px;
          }

          // Hover handled by card-base mixin

          .patient-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: #133b54;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 500;
            font-size: 14px;
            margin-right: 12px;
            flex-shrink: 0;
          }

          .patient-name {
            font-weight: 500;
            color: #1f2937;
            font-size: 14px;

            @media (max-width: $breakpoint-mobile) {
              font-size: 13px;
            }
          }
        }
      }
    }
  }

  // Appointment Type Selection
  .appointment-type-selection {
    .restriction-notice {
      background: $restriction-notice-bg-color;
      border: 1px solid $restriction-notice-border-color;
      border-radius: 8px;
      padding: 20px;
      text-align: center;

      @media (max-width: $breakpoint-mobile) {
        padding: 16px;
      }

      h3 {
        color: $restriction-notice-text-color;
        margin: 0;
        font-size: 16px;

        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }
      }
    }

    .appointment-type-list {
      display: grid;
      gap: 12px;

      .appointment-type-item {
        cursor: pointer;
        
        &.selected .appointment-type-card {
          background: #f0f9e8 !important;
          border-color: #B0D35E !important;
          color: #414a5b !important;
          font-weight: 600 !important;
        }

        .appointment-type-card {
          padding: 16px 20px;
          @include card-base;
          font-size: 16px;
          font-weight: 500;
          color: $appointment-type-text-color;
          text-align: center;

          @media (max-width: $breakpoint-mobile) {
            padding: 12px 16px;
            font-size: 14px;
          }

          // Hover handled by card-base mixin
        }
      }
    }
  }

  // Appointment Finder
  .appointment-finder {
    .finder-content {
      display: grid;
      grid-template-columns: 280px 1fr 1fr;
      gap: 32px;
      align-items: flex-start;

      @media (max-width: $breakpoint-tablet-large) {
        grid-template-columns: 1fr;
        gap: 24px;
      }
      
      @media (max-width: $breakpoint-mobile) {
        gap: 20px;
      }
    }

    .no-providers-available {
      text-align: center;
      padding: 60px 20px;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 12px;

      .no-providers-icon {
        margin-bottom: 16px;
      }

      .no-providers-title {
        font-size: 18px;
        font-weight: 600;
        color: #374151;
        margin: 0 0 12px 0;

        @media (max-width: $breakpoint-mobile) {
          font-size: 16px;
        }
      }

      .no-providers-message {
        font-size: 14px;
        color: #6b7280;
        line-height: 1.5;
        margin: 0;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;

        @media (max-width: $breakpoint-mobile) {
          font-size: 13px;
        }
      }
    }

    .selection-title {
      font-size: 16px;
      font-weight: 600;
      color: $selection-title-color;
      margin: 0 0 16px 0;

      @media (max-width: $breakpoint-mobile) {
        font-size: 14px;
      }
    }

    .provider-selection {
      .provider-list {
        display: grid;
        gap: 12px;

        @media (max-width: $breakpoint-tablet) {
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        }

        @media (max-width: $breakpoint-mobile) {
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        }

        .provider-item {
          cursor: pointer;

          &.active .provider-card {
            background: #f0f9e8 !important;
            border-color: #B0D35E !important;
            
            .provider-name {
              color: #414a5b !important;
              font-weight: 600 !important;
            }
          }

          .provider-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 16px;
            @include card-base;

            @media (max-width: $breakpoint-mobile) {
              padding: 12px;
            }

            .provider-avatar {
              width: 60px;
              height: 60px;
              border-radius: 50%;
              overflow: hidden;
              margin-bottom: 8px;
              background: #f3f4f6;
              display: flex;
              align-items: center;
              justify-content: center;

              @media (max-width: $breakpoint-mobile) {
                width: 48px;
                height: 48px;
              }

              img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
            }

            .provider-name {
              font-size: 14px;
              font-weight: 500;
              color: $provider-name-text-color;
              text-align: center;

              @media (max-width: $breakpoint-mobile) {
                font-size: 12px;
              }
            }
          }
        }
      }
    }

    .date-selection {
      // Align calendar with first element height, not header
      margin-top: 44px;
      display: flex;
      justify-content: center;

      &.no-provider {
        margin-top: 0;
      }
      
      @media (max-width: $breakpoint-tablet-large) {
        margin-top: 0;
      }
      
      .modern-date-picker {
        border: 1px solid $date-picker-border-color;
        border-radius: 12px;
        overflow: hidden;
        width: 100%;
        max-width: 400px;

        @media (max-width: $breakpoint-mobile) {
          max-width: 100%;
        }

        .v-picker__body {
          @media (max-width: $breakpoint-mobile) {
            width: 100% !important;
          }
        }

        .v-date-picker-table {
          height: auto !important;
          margin-bottom: 12px;
          min-height: 200px;
        }

        // Custom calendar styling
        .v-date-picker-title {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;

          .v-date-picker-title__year {
            margin-right: 16px !important;
          }

          .v-date-picker-title__date {
            flex: 1 !important;
            text-align: right !important;
          }
        }

        .v-picker__title {
          padding: 16px !important;
        }

        .v-date-picker-years {
          max-height: 300px !important;

          .v-btn {
            margin: 4px 0 !important;
            border-radius: 8px !important;

            &.v-btn--active {
              background-color: $stepper-step-active-bg-color !important;
              color: $stepper-step-active-text-color !important;
            }
          }
        }
      }
    }

    .time-selection {
      .time-slots {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: 12px;

        @media (max-width: $breakpoint-mobile) {
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 8px;
        }

        .time-slot {
          padding: 12px 16px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: $time-slot-text-color;
          cursor: pointer;
          transition: all 0.2s ease;

          @media (max-width: $breakpoint-mobile) {
            padding: 10px 12px;
            font-size: 12px;
          }

          &:hover {
            background-color: #edf2f7;
            border-color: #cbd5e0;
          }
          
          &.selected {
            background: #f0f9e8 !important;
            border-color: #B0D35E !important;
            color: #414a5b !important;
            font-weight: 600 !important;
          }
        }
      }

      .no-appointments {
        text-align: center;
        padding: 40px 20px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 12px;

        .no-appointments-icon {
          margin-bottom: 16px;
        }

        .no-appointments-title {
          font-size: 18px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;

          @media (max-width: $breakpoint-mobile) {
            font-size: 16px;
          }
        }

        .no-appointments-message {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
          margin: 0;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;

          @media (max-width: $breakpoint-mobile) {
            font-size: 13px;
          }
        }
      }
    }
  }

  // Booking Summary
  .booking-summary {
    .summary-section {
      margin-bottom: 24px;

      @media (max-width: $breakpoint-mobile) {
        margin-bottom: 20px;
      }

      .summary-title {
        font-size: 16px;
        font-weight: 600;
        color: $summary-title-color;
        margin: 0 0 8px 0;

        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }
      }

      .summary-content {
        color: $summary-content-color;
        font-size: 15px;

        @media (max-width: $breakpoint-mobile) {
          font-size: 14px;
        }

        .appointment-name {
          font-weight: 500;
        }

        .appointment-duration {
          color: $appointment-duration-color;
        }
      }
    }

    .notes-section {
      margin-bottom: 32px;

      @media (max-width: $breakpoint-mobile) {
        margin-bottom: 24px;
      }

      .notes-title {
        font-size: 14px;
        font-weight: 600;
        color: $notes-title-text-color;
        margin: 0 0 12px 0;

        @media (max-width: $breakpoint-mobile) {
          font-size: 13px;
        }
      }
    }

    .booking-actions {
      display: flex;
      justify-content: center;
      padding-top: 24px;

      @media (max-width: $breakpoint-mobile) {
        padding-top: 20px;

        .v-btn {
          width: 100% !important;
          max-width: none !important;
        }
      }
    }
  }
}

// Override global .v-application .selected rule
.patient-schedule .patient-item.selected {
  background-color: transparent !important;
  border-color: transparent !important;
}

.patient-schedule .appointment-type-item.selected {
  background-color: transparent !important;
  border-color: transparent !important;
}
</style>
